import { api } from "../emptyApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    initiateLogin: build.query<InitiateLoginApiResponse, InitiateLoginApiArg>({
      query: (queryArg) => ({
        url: `/auth/login`,
        params: {
          state: queryArg.state,
          screenHint: queryArg.screenHint,
        },
      }),
    }),
    handleOAuthCallback: build.query<
      HandleOAuthCallbackApiResponse,
      HandleOAuthCallbackApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/callback`,
        params: {
          code: queryArg.code,
          state: queryArg.state,
        },
      }),
    }),
    logout: build.mutation<LogoutApiResponse, LogoutApiArg>({
      query: () => ({ url: `/auth/logout`, method: "POST" }),
    }),
    getCurrentUser: build.query<
      GetCurrentUserApiResponse,
      GetCurrentUserApiArg
    >({
      query: () => ({ url: `/auth/me` }),
      providesTags: ["Auth"],
    }),
    refreshToken: build.mutation<RefreshTokenApiResponse, RefreshTokenApiArg>({
      query: (queryArg) => ({
        url: `/auth/refresh`,
        method: "POST",
        body: queryArg.body,
      }),
      invalidatesTags: ["Auth"],
    }),
    bypassAuthentication: build.mutation<
      BypassAuthenticationApiResponse,
      BypassAuthenticationApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/bypass`,
        method: "POST",
        body: queryArg.vwpAuthBypassRequest,
      }),
    }),
    changePassword: build.mutation<
      ChangePasswordApiResponse,
      ChangePasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/password`,
        method: "POST",
        body: queryArg.vwpChangePasswordRequest,
      }),
    }),
    updateProfile: build.mutation<
      UpdateProfileApiResponse,
      UpdateProfileApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/profile`,
        method: "PUT",
        body: queryArg.vwpAuthUserUpdateRequest,
      }),
      invalidatesTags: ["Auth"],
    }),
    acceptTos: build.mutation<AcceptTosApiResponse, AcceptTosApiArg>({
      query: () => ({ url: `/auth/accept-tos`, method: "POST" }),
    }),
    setup2Fa: build.mutation<Setup2FaApiResponse, Setup2FaApiArg>({
      query: () => ({ url: `/auth/2fa/setup`, method: "POST" }),
    }),
    enable2Fa: build.mutation<Enable2FaApiResponse, Enable2FaApiArg>({
      query: (queryArg) => ({
        url: `/auth/2fa/enable`,
        method: "POST",
        body: queryArg.vwp2FaEnableRequest,
      }),
    }),
    disable2Fa: build.mutation<Disable2FaApiResponse, Disable2FaApiArg>({
      query: (queryArg) => ({
        url: `/auth/2fa/disable`,
        method: "POST",
        body: queryArg.vwp2FaDisableRequest,
      }),
    }),
    initiateEmailChange: build.mutation<
      InitiateEmailChangeApiResponse,
      InitiateEmailChangeApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/email-change`,
        method: "POST",
        body: queryArg.vwpEmailChangeRequest,
      }),
      invalidatesTags: ["EmailChange"],
    }),
    getEmailChangeStatus: build.query<
      GetEmailChangeStatusApiResponse,
      GetEmailChangeStatusApiArg
    >({
      query: () => ({ url: `/auth/email-change/status` }),
      providesTags: ["EmailChange"],
    }),
    confirmEmailChange: build.mutation<
      ConfirmEmailChangeApiResponse,
      ConfirmEmailChangeApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/email-change/confirm`,
        method: "POST",
        body: queryArg.vwpEmailChangeConfirmRequest,
      }),
      invalidatesTags: ["EmailChange"],
    }),
    cancelEmailChange: build.mutation<
      CancelEmailChangeApiResponse,
      CancelEmailChangeApiArg
    >({
      query: () => ({ url: `/auth/email-change/cancel`, method: "POST" }),
      invalidatesTags: ["EmailChange"],
    }),
    createCheckoutSession: build.mutation<
      CreateCheckoutSessionApiResponse,
      CreateCheckoutSessionApiArg
    >({
      query: (queryArg) => ({
        url: `/stripe/checkout/purchase`,
        method: "POST",
        body: queryArg.vwpCreateCheckoutSessionRequest,
      }),
    }),
    getProducts: build.query<GetProductsApiResponse, GetProductsApiArg>({
      query: () => ({ url: `/stripe/products` }),
      providesTags: ["Products"],
    }),
    getPricing: build.query<GetPricingApiResponse, GetPricingApiArg>({
      query: (queryArg) => ({
        url: `/stripe/pricing`,
        headers: {
          "Accept-Currency": queryArg["Accept-Currency"],
        },
      }),
    }),
    createBillingPortalSession: build.mutation<
      CreateBillingPortalSessionApiResponse,
      CreateBillingPortalSessionApiArg
    >({
      query: (queryArg) => ({
        url: `/stripe/billing-portal`,
        method: "POST",
        body: queryArg.vwpBillingPortalRequest,
      }),
    }),
    createInvoiceSubscription: build.mutation<
      CreateInvoiceSubscriptionApiResponse,
      CreateInvoiceSubscriptionApiArg
    >({
      query: (queryArg) => ({
        url: `/stripe/subscription/invoice`,
        method: "POST",
        body: queryArg.vwpCreateInvoiceSubscriptionRequest,
        headers: {
          "Detected-Country": queryArg["Detected-Country"],
        },
      }),
    }),
    createTrialSubscription: build.mutation<
      CreateTrialSubscriptionApiResponse,
      CreateTrialSubscriptionApiArg
    >({
      query: (queryArg) => ({
        url: `/stripe/trial`,
        method: "POST",
        body: queryArg.vwpCreateTrialSubscriptionRequest,
        headers: {
          "Detected-Country": queryArg["Detected-Country"],
        },
      }),
    }),
    getCustomerRedirectUrl: build.query<
      GetCustomerRedirectUrlApiResponse,
      GetCustomerRedirectUrlApiArg
    >({
      query: (queryArg) => ({
        url: `/stripe/admin/customer-redirect`,
        params: {
          email: queryArg.email,
        },
      }),
    }),
    identifyHubspotUser: build.mutation<
      IdentifyHubspotUserApiResponse,
      IdentifyHubspotUserApiArg
    >({
      query: () => ({ url: `/hubspot/identify-visitor`, method: "POST" }),
    }),
    getUsers: build.query<GetUsersApiResponse, GetUsersApiArg>({
      query: (queryArg) => ({
        url: `/admin/users`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          search: queryArg.search,
          sort: queryArg.sort,
        },
      }),
      providesTags: ["Users"],
    }),
    createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
      query: (queryArg) => ({
        url: `/admin/users`,
        method: "POST",
        body: queryArg.vwpUserCreationRequest,
      }),
      invalidatesTags: ["Users"],
    }),
    updateUserById: build.mutation<
      UpdateUserByIdApiResponse,
      UpdateUserByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/users/${queryArg.id}`,
        method: "PUT",
        body: queryArg.vwpUserUpdateRequest,
      }),
      invalidatesTags: ["Users"],
    }),
    updateUserEmailById: build.mutation<
      UpdateUserEmailByIdApiResponse,
      UpdateUserEmailByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/users/${queryArg.id}/email`,
        method: "PUT",
        body: queryArg.vwpUserEmailChangeRequest,
      }),
      invalidatesTags: ["Users"],
    }),
    assignUserToTeam: build.mutation<
      AssignUserToTeamApiResponse,
      AssignUserToTeamApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/users/${queryArg.userId}/team`,
        method: "PUT",
        body: queryArg.vwpUserTeamChangeRequest,
      }),
      invalidatesTags: ["Users", "Admin", "PortalTeam"],
    }),
    updateUserRole: build.mutation<
      UpdateUserRoleApiResponse,
      UpdateUserRoleApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/users/${queryArg.email}/role`,
        method: "PUT",
        body: queryArg.vwpUpdateUserRoleRequest,
      }),
      invalidatesTags: ["Users", "PortalTeam"],
    }),
    removeTeamMember: build.mutation<
      RemoveTeamMemberApiResponse,
      RemoveTeamMemberApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/team/members/${queryArg.userEmail}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PortalTeam"],
    }),
    getPortalTeam: build.query<GetPortalTeamApiResponse, GetPortalTeamApiArg>({
      query: (queryArg) => ({
        url: `/portal/team`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          search: queryArg.search,
        },
      }),
      providesTags: ["PortalTeam"],
    }),
    updatePortalTeam: build.mutation<
      UpdatePortalTeamApiResponse,
      UpdatePortalTeamApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/team`,
        method: "PUT",
        body: queryArg.vwpTeamUpdateRequest,
      }),
    }),
    getPortalTeamArchivedMembers: build.query<
      GetPortalTeamArchivedMembersApiResponse,
      GetPortalTeamArchivedMembersApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/team/archived-members`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
        },
      }),
      providesTags: ["PortalTeam"],
    }),
    removeLicenseAssignment: build.mutation<
      RemoveLicenseAssignmentApiResponse,
      RemoveLicenseAssignmentApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/license-assignment`,
        method: "DELETE",
        body: queryArg.vwpRemoveLicenseAssignmentRequest,
      }),
      invalidatesTags: ["PortalLicenses", "PortalTeam"],
    }),
    addLicenseAssignment: build.mutation<
      AddLicenseAssignmentApiResponse,
      AddLicenseAssignmentApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/license-assignment`,
        method: "POST",
        body: queryArg.vwpAddLicenseAssignmentRequest,
      }),
      invalidatesTags: ["PortalLicenses", "PortalTeam"],
    }),
    getPortalLicenses: build.query<
      GetPortalLicensesApiResponse,
      GetPortalLicensesApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/licenses`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          search: queryArg.search,
        },
      }),
      providesTags: ["PortalLicenses"],
    }),
    getLicenses: build.query<GetLicensesApiResponse, GetLicensesApiArg>({
      query: (queryArg) => ({
        url: `/admin/licenses`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          search: queryArg.search,
          sort: queryArg.sort,
        },
      }),
      providesTags: ["Admin"],
    }),
    createLicense: build.mutation<
      CreateLicenseApiResponse,
      CreateLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/licenses`,
        method: "POST",
        body: queryArg.vwpCreateLicenseRequest,
      }),
      invalidatesTags: ["Admin"],
    }),
    getLicenseById: build.query<
      GetLicenseByIdApiResponse,
      GetLicenseByIdApiArg
    >({
      query: (queryArg) => ({ url: `/admin/licenses/${queryArg.licenseId}` }),
      providesTags: ["Admin"],
    }),
    updateLicenseById: build.mutation<
      UpdateLicenseByIdApiResponse,
      UpdateLicenseByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/licenses/${queryArg.licenseId}`,
        method: "PUT",
        body: queryArg.vwpUpdateLicenseRequest,
      }),
      invalidatesTags: ["Admin"],
    }),
    updateLicenseQuotaUsage: build.mutation<
      UpdateLicenseQuotaUsageApiResponse,
      UpdateLicenseQuotaUsageApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/licenses/${queryArg.licenseId}/quotas/${queryArg.entitlement}`,
        method: "PUT",
        body: queryArg.vwpUpdateQuotaUsageRequest,
      }),
      invalidatesTags: ["Admin", "PortalLicenses"],
    }),
    assignLicenseToTeam: build.mutation<
      AssignLicenseToTeamApiResponse,
      AssignLicenseToTeamApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/licenses/${queryArg.licenseId}/team`,
        method: "PUT",
        body: queryArg.vwpAssignLicenseToTeamRequest,
      }),
      invalidatesTags: ["Admin", "PortalLicenses", "PortalTeam"],
    }),
    getLicenseStripeUrl: build.query<
      GetLicenseStripeUrlApiResponse,
      GetLicenseStripeUrlApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/licenses/${queryArg.licenseId}/stripe-url`,
      }),
    }),
    migrateLicenseToStripe: build.mutation<
      MigrateLicenseToStripeApiResponse,
      MigrateLicenseToStripeApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/licenses/${queryArg.licenseId}/migrate-to-stripe`,
        method: "PUT",
        body: queryArg.vwpMigrateLicenseToStripeRequest,
      }),
    }),
    removeTeamMemberByAdmin: build.mutation<
      RemoveTeamMemberByAdminApiResponse,
      RemoveTeamMemberByAdminApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/teams/${queryArg.teamId}/members/${queryArg.memberEmail}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Admin", "PortalTeam"],
    }),
    getTeams: build.query<GetTeamsApiResponse, GetTeamsApiArg>({
      query: (queryArg) => ({
        url: `/admin/teams`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          search: queryArg.search,
          sort: queryArg.sort,
        },
      }),
      providesTags: ["Admin"],
    }),
    createTeam: build.mutation<CreateTeamApiResponse, CreateTeamApiArg>({
      query: (queryArg) => ({
        url: `/admin/teams`,
        method: "POST",
        body: queryArg.vwpCreateTeamRequest,
      }),
      invalidatesTags: ["Admin"],
    }),
    getTeamById: build.query<GetTeamByIdApiResponse, GetTeamByIdApiArg>({
      query: (queryArg) => ({ url: `/admin/teams/${queryArg.teamId}` }),
      providesTags: ["Admin"],
    }),
    updateTeamById: build.mutation<
      UpdateTeamByIdApiResponse,
      UpdateTeamByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/teams/${queryArg.teamId}`,
        method: "PUT",
        body: queryArg.vwpTeamUpdateRequest,
      }),
      invalidatesTags: ["Admin"],
    }),
    getTeamArchivedMembersById: build.query<
      GetTeamArchivedMembersByIdApiResponse,
      GetTeamArchivedMembersByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/teams/${queryArg.teamId}/archived-members`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
        },
      }),
      providesTags: ["Admin"],
    }),
    getAdminProducts: build.query<
      GetAdminProductsApiResponse,
      GetAdminProductsApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/products`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          search: queryArg.search,
          sort: queryArg.sort,
        },
      }),
      providesTags: ["Products"],
    }),
    updateProductById: build.mutation<
      UpdateProductByIdApiResponse,
      UpdateProductByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/products/${queryArg.productId}`,
        method: "PUT",
        body: queryArg.vwpUpdateProductRequest,
      }),
      invalidatesTags: ["Products"],
    }),
    addTeamMemberLicense: build.mutation<
      AddTeamMemberLicenseApiResponse,
      AddTeamMemberLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/teams/${queryArg.teamId}/members/${queryArg.memberEmail}/licenses`,
        method: "POST",
        body: queryArg.vwpAddLicenseAssignmentRequest,
      }),
      invalidatesTags: ["PortalLicenses", "PortalTeam", "Admin", "Users"],
    }),
    removeTeamMemberLicense: build.mutation<
      RemoveTeamMemberLicenseApiResponse,
      RemoveTeamMemberLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/admin/teams/${queryArg.teamId}/members/${queryArg.memberEmail}/licenses`,
        method: "DELETE",
        params: {
          licenseId: queryArg.licenseId,
        },
      }),
      invalidatesTags: ["PortalLicenses", "PortalTeam", "Admin", "Users"],
    }),
    generateInvitation: build.mutation<
      GenerateInvitationApiResponse,
      GenerateInvitationApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/invitations/generate`,
        method: "POST",
        body: queryArg.vwpGenerateInvitationRequest,
      }),
    }),
    redeemInvitation: build.mutation<
      RedeemInvitationApiResponse,
      RedeemInvitationApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/invitations/redeem`,
        method: "POST",
        body: queryArg.vwpRedeemInvitationRequest,
      }),
    }),
    getPortalConfig: build.query<
      GetPortalConfigApiResponse,
      GetPortalConfigApiArg
    >({
      query: () => ({ url: `/portal/config` }),
      providesTags: ["Portal"],
    }),
    initiateAlumniAcceptance: build.mutation<
      InitiateAlumniAcceptanceApiResponse,
      InitiateAlumniAcceptanceApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/accept-alumni/initiate`,
        method: "POST",
        body: queryArg.vwpAlumniAcceptInitiateRequest,
      }),
      invalidatesTags: ["AlumniAcceptance"],
    }),
    getAlumniAcceptanceStatus: build.query<
      GetAlumniAcceptanceStatusApiResponse,
      GetAlumniAcceptanceStatusApiArg
    >({
      query: () => ({ url: `/portal/accept-alumni/status` }),
      providesTags: ["AlumniAcceptance"],
    }),
    confirmAlumniAcceptance: build.mutation<
      ConfirmAlumniAcceptanceApiResponse,
      ConfirmAlumniAcceptanceApiArg
    >({
      query: (queryArg) => ({
        url: `/portal/accept-alumni/confirm`,
        method: "POST",
        body: queryArg.vwpAlumniAcceptConfirmRequest,
      }),
      invalidatesTags: ["AlumniAcceptance", "Portal"],
    }),
    cancelAlumniAcceptance: build.mutation<
      CancelAlumniAcceptanceApiResponse,
      CancelAlumniAcceptanceApiArg
    >({
      query: () => ({ url: `/portal/accept-alumni/cancel`, method: "POST" }),
      invalidatesTags: ["AlumniAcceptance"],
    }),
    getWebsiteContent: build.query<
      GetWebsiteContentApiResponse,
      GetWebsiteContentApiArg
    >({
      query: (queryArg) => ({
        url: `/website/content`,
        params: {
          url: queryArg.url,
        },
      }),
    }),
    getAdminEntitlements: build.query<
      GetAdminEntitlementsApiResponse,
      GetAdminEntitlementsApiArg
    >({
      query: () => ({ url: `/admin/entitlements` }),
    }),
    createWebhookStripeInvoice: build.mutation<
      CreateWebhookStripeInvoiceApiResponse,
      CreateWebhookStripeInvoiceApiArg
    >({
      query: (queryArg) => ({
        url: `/webhook/stripe-invoice`,
        method: "POST",
        body: queryArg.vwpWebhookCreateInvoiceRequest,
        headers: {
          "Idempotency-Key": queryArg["Idempotency-Key"],
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as emptyApi };
export type InitiateLoginApiResponse = unknown;
export type InitiateLoginApiArg = {
  /** Optional state parameter containing JSON with redirect URL for post-authentication redirect */
  state?: string;
  /** Optional AuthKit screen to show first; defaults to sign-in */
  screenHint?: "sign-up" | "sign-in";
};
export type HandleOAuthCallbackApiResponse = unknown;
export type HandleOAuthCallbackApiArg = {
  /** Authorization code from WorkOS */
  code: string;
  /** Optional state parameter for CSRF protection. If not provided, redirects to /dashboard */
  state?: string;
};
export type LogoutApiResponse =
  /** status 200 Logout successful */ VwpLogoutResponse;
export type LogoutApiArg = void;
export type GetCurrentUserApiResponse =
  /** status 200 Current user information */ VwpGetCurrentUserResponse;
export type GetCurrentUserApiArg = void;
export type RefreshTokenApiResponse =
  /** status 200 Token refreshed successfully */ VwpRefreshTokenResponse;
export type RefreshTokenApiArg = {
  body: {
    refreshToken: string;
  };
};
export type BypassAuthenticationApiResponse =
  /** status 200 Authentication bypass data stored successfully */ VwpAuthBypassResponse;
export type BypassAuthenticationApiArg = {
  vwpAuthBypassRequest: VwpAuthBypassRequest;
};
export type ChangePasswordApiResponse = unknown;
export type ChangePasswordApiArg = {
  vwpChangePasswordRequest: VwpChangePasswordRequest;
};
export type UpdateProfileApiResponse =
  /** status 200 Profile updated successfully */ VwpAuthUser;
export type UpdateProfileApiArg = {
  vwpAuthUserUpdateRequest: VwpAuthUserUpdateRequest;
};
export type AcceptTosApiResponse = unknown;
export type AcceptTosApiArg = void;
export type Setup2FaApiResponse =
  /** status 200 2FA setup initiated successfully */ Vwp2FaSetupResponse;
export type Setup2FaApiArg = void;
export type Enable2FaApiResponse =
  /** status 200 2FA enabled successfully */ Vwp2FaEnableResponse;
export type Enable2FaApiArg = {
  vwp2FaEnableRequest: Vwp2FaEnableRequest;
};
export type Disable2FaApiResponse =
  /** status 200 2FA disabled successfully */ Vwp2FaDisableResponse;
export type Disable2FaApiArg = {
  vwp2FaDisableRequest: Vwp2FaDisableRequest;
};
export type InitiateEmailChangeApiResponse =
  /** status 200 Email change initiated */ VwpEmailChangeResponse;
export type InitiateEmailChangeApiArg = {
  vwpEmailChangeRequest: VwpEmailChangeRequest;
};
export type GetEmailChangeStatusApiResponse =
  /** status 200 Current email change state */ VwpEmailChangeResponse;
export type GetEmailChangeStatusApiArg = void;
export type ConfirmEmailChangeApiResponse =
  /** status 200 Email change confirmed */ VwpEmailChangeResponse;
export type ConfirmEmailChangeApiArg = {
  vwpEmailChangeConfirmRequest: VwpEmailChangeConfirmRequest;
};
export type CancelEmailChangeApiResponse =
  /** status 200 Email change cancelled */ VwpEmailChangeResponse;
export type CancelEmailChangeApiArg = void;
export type CreateCheckoutSessionApiResponse =
  /** status 200 Checkout session created successfully */ VwpCheckoutSessionResponse;
export type CreateCheckoutSessionApiArg = {
  vwpCreateCheckoutSessionRequest: VwpCreateCheckoutSessionRequest;
};
export type GetProductsApiResponse =
  /** status 200 Product catalog retrieved successfully */ VwpStripeProductsResponse;
export type GetProductsApiArg = void;
export type GetPricingApiResponse =
  /** status 200 Pricing information retrieved successfully */ VwpPricingResponse;
export type GetPricingApiArg = {
  /** Preferred currency code (e.g., CHF, EUR, USD). If not provided, returns all prices. */
  "Accept-Currency"?: string;
};
export type CreateBillingPortalSessionApiResponse =
  /** status 200 Billing portal session created successfully */ VwpBillingPortalResponse;
export type CreateBillingPortalSessionApiArg = {
  vwpBillingPortalRequest: VwpBillingPortalRequest;
};
export type CreateInvoiceSubscriptionApiResponse =
  /** status 200 Subscription created successfully */ VwpInvoiceSubscriptionResponse;
export type CreateInvoiceSubscriptionApiArg = {
  /** ISO 3166-1 alpha-2 country code detected from the client (e.g. CH). Used to pre-populate the team's billing country if not already set. */
  "Detected-Country"?: string;
  vwpCreateInvoiceSubscriptionRequest: VwpCreateInvoiceSubscriptionRequest;
};
export type CreateTrialSubscriptionApiResponse =
  /** status 200 Trial subscription created successfully */ VwpInvoiceSubscriptionResponse;
export type CreateTrialSubscriptionApiArg = {
  /** ISO 3166-1 alpha-2 country code detected from the client (e.g. CH). Used to pre-populate the team's billing country if not already set. */
  "Detected-Country"?: string;
  vwpCreateTrialSubscriptionRequest: VwpCreateTrialSubscriptionRequest;
};
export type GetCustomerRedirectUrlApiResponse =
  /** status 200 Customer redirect URL retrieved successfully */ VwpCustomerRedirectResponse;
export type GetCustomerRedirectUrlApiArg = {
  /** User email to get Stripe customer for */
  email: string;
};
export type IdentifyHubspotUserApiResponse =
  /** status 200 The response from Hubspot which contains a token to identify the current user session.
And Hubspot interactions that are triggered by the user portal (e.g. chat) should use this session
information.
 */ VwpHubspotIdentifyUserResponse;
export type IdentifyHubspotUserApiArg = void;
export type GetUsersApiResponse =
  /** status 200 Users retrieved successfully */ VwpUserListResponseRead;
export type GetUsersApiArg = {
  /** Page number (0-based indexing) */
  page?: number;
  /** Number of items per page */
  size?: number;
  /** Filters users by name or email (case-insensitive) */
  search?: string;
  /** Sort field and direction (format: field,direction) */
  sort?: string;
};
export type CreateUserApiResponse =
  /** status 200 User created successfully */ VwpUserRead;
export type CreateUserApiArg = {
  vwpUserCreationRequest: VwpUserCreationRequest;
};
export type UpdateUserByIdApiResponse =
  /** status 200 User updated successfully */ VwpUserRead;
export type UpdateUserByIdApiArg = {
  /** User ID to update */
  id: number;
  vwpUserUpdateRequest: VwpUserUpdateRequest;
};
export type UpdateUserEmailByIdApiResponse =
  /** status 200 User email updated successfully */ VwpUserRead;
export type UpdateUserEmailByIdApiArg = {
  /** User ID to update */
  id: number;
  vwpUserEmailChangeRequest: VwpUserEmailChangeRequest;
};
export type AssignUserToTeamApiResponse =
  /** status 200 User assigned to team successfully */ VwpUserTeamChangeResponse;
export type AssignUserToTeamApiArg = {
  /** User ID to assign to team */
  userId: number;
  vwpUserTeamChangeRequest: VwpUserTeamChangeRequest;
};
export type UpdateUserRoleApiResponse =
  /** status 200 User role updated successfully */ VwpUserRead;
export type UpdateUserRoleApiArg = {
  /** Email address of the user to update */
  email: string;
  vwpUpdateUserRoleRequest: VwpUpdateUserRoleRequest;
};
export type RemoveTeamMemberApiResponse =
  /** status 200 User removed from team successfully */ VwpTeamResponseRead;
export type RemoveTeamMemberApiArg = {
  /** Email address of the user to remove from the team */
  userEmail: string;
};
export type GetPortalTeamApiResponse =
  /** status 200 Team information retrieved successfully */ VwpTeamResponseRead;
export type GetPortalTeamApiArg = {
  /** Page number for team members (0-based indexing) */
  page?: number;
  /** Number of team members per page */
  size?: number;
  /** Filters team members by name or email (case-insensitive) */
  search?: string;
};
export type UpdatePortalTeamApiResponse =
  /** status 200 Team updated successfully */ VwpTeamResponseRead;
export type UpdatePortalTeamApiArg = {
  vwpTeamUpdateRequest: VwpTeamUpdateRequest;
};
export type GetPortalTeamArchivedMembersApiResponse =
  /** status 200 Archived team members retrieved successfully */ VwpArchivedMembers;
export type GetPortalTeamArchivedMembersApiArg = {
  /** Page number for archived members (0-based indexing) */
  page?: number;
  /** Number of archived members per page */
  size?: number;
};
export type RemoveLicenseAssignmentApiResponse =
  /** status 200 License assignment removed successfully */ VwpRemoveLicenseAssignmentResponseRead;
export type RemoveLicenseAssignmentApiArg = {
  vwpRemoveLicenseAssignmentRequest: VwpRemoveLicenseAssignmentRequest;
};
export type AddLicenseAssignmentApiResponse =
  /** status 200 License assignment created successfully */ VwpAddLicenseAssignmentResponseRead;
export type AddLicenseAssignmentApiArg = {
  vwpAddLicenseAssignmentRequest: VwpAddLicenseAssignmentRequest;
};
export type GetPortalLicensesApiResponse =
  /** status 200 Licenses retrieved successfully */ VwpLicenseListResponseRead;
export type GetPortalLicensesApiArg = {
  /** Page number for licenses (0-based indexing) */
  page?: number;
  /** Number of licenses per page */
  size?: number;
  /** Filters licenses by product name (case-insensitive) */
  search?: string;
};
export type GetLicensesApiResponse =
  /** status 200 Licenses retrieved successfully */ VwpLicenseListResponseRead;
export type GetLicensesApiArg = {
  /** Page number (0-based indexing) */
  page?: number;
  /** Number of items per page */
  size?: number;
  /** Filters licenses by product name or team name (case-insensitive) */
  search?: string;
  /** Sort field and direction (format: field,direction) */
  sort?: string;
};
export type CreateLicenseApiResponse =
  /** status 200 License created successfully */ VwpLicenseRead;
export type CreateLicenseApiArg = {
  vwpCreateLicenseRequest: VwpCreateLicenseRequest;
};
export type GetLicenseByIdApiResponse =
  /** status 200 License retrieved successfully */ VwpLicenseRead;
export type GetLicenseByIdApiArg = {
  /** License identifier */
  licenseId: string;
};
export type UpdateLicenseByIdApiResponse =
  /** status 200 License updated successfully */ VwpLicenseRead;
export type UpdateLicenseByIdApiArg = {
  /** License identifier to update */
  licenseId: string;
  vwpUpdateLicenseRequest: VwpUpdateLicenseRequest;
};
export type UpdateLicenseQuotaUsageApiResponse =
  /** status 200 Quota usage updated successfully */ VwpQuota;
export type UpdateLicenseQuotaUsageApiArg = {
  /** License identifier */
  licenseId: string;
  /** Name of the quota-enabled entitlement */
  entitlement: string;
  vwpUpdateQuotaUsageRequest: VwpUpdateQuotaUsageRequest;
};
export type AssignLicenseToTeamApiResponse =
  /** status 200 License assigned to team successfully */ VwpAssignLicenseToTeamResponseRead;
export type AssignLicenseToTeamApiArg = {
  /** License identifier */
  licenseId: string;
  vwpAssignLicenseToTeamRequest: VwpAssignLicenseToTeamRequest;
};
export type GetLicenseStripeUrlApiResponse =
  /** status 200 Stripe URL retrieved successfully */ VwpStripeUrlResponse;
export type GetLicenseStripeUrlApiArg = {
  /** License identifier */
  licenseId: string;
};
export type MigrateLicenseToStripeApiResponse =
  /** status 200 License migrated successfully */ VwpLicenseRead;
export type MigrateLicenseToStripeApiArg = {
  /** License identifier to migrate */
  licenseId: string;
  vwpMigrateLicenseToStripeRequest: VwpMigrateLicenseToStripeRequest;
};
export type RemoveTeamMemberByAdminApiResponse =
  /** status 200 User removed from team successfully */ VwpTeamResponseRead;
export type RemoveTeamMemberByAdminApiArg = {
  /** Team identifier */
  teamId: string;
  /** Email address of team member to remove */
  memberEmail: string;
};
export type GetTeamsApiResponse =
  /** status 200 Teams retrieved successfully */ VwpTeamListResponseRead;
export type GetTeamsApiArg = {
  /** Page number (0-based indexing) */
  page?: number;
  /** Number of items per page */
  size?: number;
  /** Filters teams by name or admin email (case-insensitive) */
  search?: string;
  /** Sort field and direction (format: field,direction) */
  sort?: string;
};
export type CreateTeamApiResponse =
  /** status 200 Team created successfully */ VwpCreateTeamResponseRead;
export type CreateTeamApiArg = {
  vwpCreateTeamRequest: VwpCreateTeamRequest;
};
export type GetTeamByIdApiResponse =
  /** status 200 Team retrieved successfully */ VwpTeamRead;
export type GetTeamByIdApiArg = {
  /** Team identifier */
  teamId: string;
};
export type UpdateTeamByIdApiResponse =
  /** status 200 Team updated successfully */ VwpTeamResponseRead;
export type UpdateTeamByIdApiArg = {
  /** Team identifier */
  teamId: string;
  vwpTeamUpdateRequest: VwpTeamUpdateRequest;
};
export type GetTeamArchivedMembersByIdApiResponse =
  /** status 200 Archived team members retrieved successfully */ VwpArchivedMembers;
export type GetTeamArchivedMembersByIdApiArg = {
  /** Team identifier */
  teamId: string;
  /** Page number for archived members (0-based indexing) */
  page?: number;
  /** Number of archived members per page */
  size?: number;
};
export type GetAdminProductsApiResponse =
  /** status 200 Products retrieved successfully */ VwpProductListResponse;
export type GetAdminProductsApiArg = {
  /** Page number (0-based indexing) */
  page?: number;
  /** Number of items per page */
  size?: number;
  /** Filters products by name or description (case-insensitive) */
  search?: string;
  /** Sort field and direction (format: field,direction) */
  sort?: string;
};
export type UpdateProductByIdApiResponse =
  /** status 200 Product updated successfully */ VwpProduct;
export type UpdateProductByIdApiArg = {
  /** Product identifier to update */
  productId: string;
  vwpUpdateProductRequest: VwpUpdateProductRequest;
};
export type AddTeamMemberLicenseApiResponse =
  /** status 200 License assigned successfully */ VwpAddLicenseAssignmentResponseRead;
export type AddTeamMemberLicenseApiArg = {
  /** Team identifier */
  teamId: string;
  /** Email address of team member */
  memberEmail: string;
  vwpAddLicenseAssignmentRequest: VwpAddLicenseAssignmentRequest;
};
export type RemoveTeamMemberLicenseApiResponse =
  /** status 200 License removed successfully */ VwpRemoveLicenseAssignmentResponseRead;
export type RemoveTeamMemberLicenseApiArg = {
  /** Team identifier */
  teamId: string;
  /** Email address of team member */
  memberEmail: string;
  /** ID of license to remove */
  licenseId: string;
};
export type GenerateInvitationApiResponse =
  /** status 200 Invitation code generated successfully */ VwpGenerateInvitationResponse;
export type GenerateInvitationApiArg = {
  vwpGenerateInvitationRequest: VwpGenerateInvitationRequest;
};
export type RedeemInvitationApiResponse =
  /** status 200 Invitation redeemed successfully */ VwpRedeemInvitationResponse;
export type RedeemInvitationApiArg = {
  vwpRedeemInvitationRequest: VwpRedeemInvitationRequest;
};
export type GetPortalConfigApiResponse =
  /** status 200 Feature configuration retrieved successfully */ VwpFeatureConfig;
export type GetPortalConfigApiArg = void;
export type InitiateAlumniAcceptanceApiResponse =
  /** status 200 Alumni acceptance initiated; awaiting email verification */ VwpEmailChangeResponse;
export type InitiateAlumniAcceptanceApiArg = {
  vwpAlumniAcceptInitiateRequest: VwpAlumniAcceptInitiateRequest;
};
export type GetAlumniAcceptanceStatusApiResponse =
  /** status 200 Current alumni acceptance state */ VwpEmailChangeResponse;
export type GetAlumniAcceptanceStatusApiArg = void;
export type ConfirmAlumniAcceptanceApiResponse =
  /** status 200 Alumni acceptance confirmed and applied */ VwpEmailChangeResponse;
export type ConfirmAlumniAcceptanceApiArg = {
  vwpAlumniAcceptConfirmRequest: VwpAlumniAcceptConfirmRequest;
};
export type CancelAlumniAcceptanceApiResponse =
  /** status 200 Alumni acceptance cancelled */ VwpEmailChangeResponse;
export type CancelAlumniAcceptanceApiArg = void;
export type GetWebsiteContentApiResponse =
  /** status 200 HTML content retrieved successfully */ string;
export type GetWebsiteContentApiArg = {
  /** Full URL to fetch HTML content from */
  url: string;
};
export type GetAdminEntitlementsApiResponse =
  /** status 200 Successful retrieval of available entitlements */ VwpEntitlementsResponse;
export type GetAdminEntitlementsApiArg = void;
export type CreateWebhookStripeInvoiceApiResponse =
  /** status 200 Invoice or subscription created successfully */ VwpInvoiceSubscriptionResponse;
export type CreateWebhookStripeInvoiceApiArg = {
  /** Optional idempotency key forwarded to Stripe to avoid duplicate invoice/subscription creation on retries. */
  "Idempotency-Key"?: string;
  vwpWebhookCreateInvoiceRequest: VwpWebhookCreateInvoiceRequest;
};
export type VwpLogoutResponse = {
  /** URL to redirect to for logout */
  redirectUrl?: string;
};
export type VwpUserFlag =
  | "BYPASS_HARDWARE_LOCKS"
  | "HAS_ACCEPTED_TOS"
  | "WAS_STUDENT"
  | "ALUMNI_ELIGIBLE"
  | "IS_ALUMNI";
export type VwpAuthUser = {
  /** Internal user ID from WorkOS */
  id: string;
  /** User email address */
  email: string;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** User roles */
  roles: string[];
  /** User creation timestamp */
  created_at: string;
  /** Last update timestamp */
  updated_at: string;
  /** Last login timestamp */
  last_login?: string;
  /** Last login on this platform as stored on the license server */
  lastPlatformLogin?: string;
  /** Last login inside Polysun that triggered a license check */
  lastLicenseLogin?: string;
  /** Last used Polysun version from license server */
  lastUsedVersion?: string;
  /** Whether the user account is active */
  active: boolean;
  /** URL to the user's profile picture */
  avatar?: string;
  flags?: VwpUserFlag[];
  /** Whether two-factor authentication is enabled for the user */
  twoFactorEnabled?: boolean;
  /** Original WorkOS access token for 2FA widget integration */
  workosToken?: string;
  language?: string;
};
export type VwpGetCurrentUserResponse = {
  user?: VwpAuthUser;
};
export type VwpRefreshTokenResponse = {
  /** New JWT access token */
  token?: string;
  /** New JWT refresh token */
  refresh_token?: string;
  /** Token expiration time in seconds */
  expires_in?: number;
  /** WorkOS refresh token for subsequent token refreshes */
  workos_refresh_token?: string;
};
export type VwpAuthBypassResponse = {
  user?: VwpAuthUser;
};
export type VwpAuthBypassRequest = {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  roles: string[];
  permissions: string[];
  active: boolean;
  avatar?: string;
};
export type VwpErrorResponse = {
  success: boolean;
  /** Error code */
  error: string;
  /** Human-readable error message */
  message: string;
  /** Additional error details */
  details?: {
    /** Field-specific validation errors */
    validationErrors?: {
      [key: string]: string[];
    };
  };
};
export type VwpChangePasswordRequest = {
  /** User's current password */
  currentPassword: string;
  /** User's new password */
  newPassword: string;
};
export type VwpAuthUserUpdateRequest = {
  firstName?: string;
  lastName?: string;
  /** Deprecated — email changes are rejected here. Use POST /auth/email-change instead.
   */
  email?: string;
  language?: string;
};
export type Vwp2FaSetupResponse = {
  /** TOTP secret for 2FA setup */
  secret: string;
  /** QR code URI for scanning with authenticator app */
  qrCodeUri: string;
};
export type Vwp2FaEnableResponse = {
  /** Success message */
  message: string;
};
export type Vwp2FaEnableRequest = {
  /** 6-digit verification code from authenticator app */
  code: string;
};
export type Vwp2FaDisableResponse = {
  /** Success message */
  message: string;
};
export type Vwp2FaDisableRequest = {
  /** User's password for confirmation */
  password: string;
};
export type VwpEmailChangeStatus =
  | "PENDING"
  | "AWAITING_VERIFICATION"
  | "COMPLETED"
  | "ROLLED_BACK"
  | "CANCELLED"
  | "FAILED";
export type VwpEmailChangeResponse = {
  changeId?: number;
  status?: VwpEmailChangeStatus;
  oldEmail?: string;
  newEmail?: string;
  expiresAt?: string;
  completedAt?: string;
};
export type VwpEmailChangeRequest = {
  newEmail: string;
};
export type VwpEmailChangeConfirmRequest = {
  /** One-time code from the verification email sent to the new address */
  code: string;
};
export type VwpCheckoutSessionResponse = {
  /** Stripe checkout session ID */
  sessionId?: string;
  /** URL to redirect to for Stripe checkout */
  sessionUrl?: string;
};
export type VwpCustomerType = "private" | "business";
export type VwpCreateCheckoutSessionRequest = {
  /** Product key that maps to product configuration */
  productKey:
    | "standard"
    | "designer"
    | "designer_alumni"
    | "education_student"
    | "education";
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string;
  trial?: boolean;
  quantity?: number;
  metadata?: {
    [key: string]: string;
  };
  /** Optional discount code to apply to the checkout session */
  discountCode?: string;
  customerType?: VwpCustomerType;
  /** VAT number required for business checkout purchases */
  vatNumber?: string;
};
export type StripeProductType = "one_time" | "subscription";
export type StripeBillingInterval = "day" | "week" | "month" | "year";
export type VwpStripePrice = {
  /** Stripe price identifier */
  id: string;
  /** Stripe product identifier */
  productId: string;
  /** Stripe price nickname */
  nickname?: string;
  /** Currency code (ISO 4217) */
  currency: string;
  /** Price in smallest currency unit (cents) */
  unitAmount: number;
  /** Stripe recurring billing information */
  recurring?: {
    interval?: StripeBillingInterval;
    /** Number of intervals between charges */
    intervalCount?: number;
  };
  /** Stripe trial period in days (if applicable) */
  trialPeriodDays?: number;
  /** Additional Stripe price metadata */
  metadata?: {
    [key: string]: string;
  };
};
export type VwpStripeProduct = {
  /** Stripe product identifier */
  id: string;
  /** Stripe product name */
  name: string;
  /** Stripe product description */
  description: string;
  type: StripeProductType;
  /** List of product features */
  features?: string[];
  /** Additional Stripe product metadata */
  metadata?: {
    [key: string]: string;
  };
  /** List of Stripe prices for the product */
  prices: VwpStripePrice[];
};
export type VwpStripeProductsResponse = {
  /** List of available Stripe products with their prices */
  products?: VwpStripeProduct[];
};
export type VwpPricingTier = {
  upTo?: number;
  unitAmount?: number;
  flatAmount?: number;
};
export type VwpPricingPrice = {
  /** Price in smallest currency unit (cents) */
  amount?: number;
  /** Currency code (ISO 4217) */
  currency?: string;
  /** Billing interval (for subscription prices) */
  interval?: string;
  /** Tiered pricing information (present for tiered pricing) */
  tiers?: VwpPricingTier[];
};
export type VwpPricing = {
  /** Product key from ProductConfig for frontend matching */
  productKey?: string;
  /** Product identifier */
  productId: string;
  licenseServerProductId?: string;
  /** Product name from ProductConfig */
  productName: string;
  purchasePrice: VwpPricingPrice;
  /** Discounted purchase price for subsequent purchases (not first purchase) */
  discountedPurchasePrice?: VwpPricingPrice;
  supportPrice: VwpPricingPrice;
  supportPriceDiscounted?: VwpPricingPrice;
  /** Number of licenses in package (for education products) */
  packageSize?: number;
  /** License duration in months */
  licenseDurationMonths?: number;
  /** Human-readable license duration label */
  licenseDurationLabel?: string;
  /** Type of pricing display (monthly_first_year, monthly_6month, package) */
  pricingType?: string;
  /** Whether this is the user's first purchase of this product */
  isFirstPurchase?: boolean;
  /** Whether the user is eligible for trial for this product */
  trialEligible?: boolean;
  /** Whether the user has an active, non-expired license for this product */
  hasValidLicense?: boolean;
};
export type VwpPricingResponse = {
  /** List of products with pricing information */
  products?: VwpPricing[];
};
export type VwpBillingPortalResponse = {
  /** URL to redirect to for Stripe billing portal */
  sessionUrl?: string;
};
export type VwpBillingPortalRequest = {
  /** URL to redirect to after billing portal session */
  returnUrl: string;
};
export type VwpInvoiceSubscriptionResponse = {
  /** Stripe subscription ID */
  subscriptionId?: string;
  /** Status of the subscription */
  subscriptionStatus?: string;
  /** Start timestamp of current billing period */
  currentPeriodStart?: number;
  /** End timestamp of current billing period */
  currentPeriodEnd?: number;
  /** Stripe customer ID */
  customerId?: string;
  /** Stripe invoice ID */
  invoiceId?: string;
  /** URL to redirect user after trial subscription creation */
  redirectUrl?: string;
};
export type VwpCreateInvoiceSubscriptionRequest = {
  /** Product key that maps to product configuration */
  productKey:
    | "standard"
    | "designer"
    | "designer_alumni"
    | "education_student"
    | "education";
  /** Number of subscriptions to create */
  quantity?: number;
  /** Optional discount code to apply to invoice subscription */
  discountCode?: string;
  customerType: VwpCustomerType;
  /** VAT number for business invoice purchases */
  vatNumber?: string;
};
export type VwpCreateTrialSubscriptionRequest = {
  /** Product key that maps to product configuration */
  productKey:
    | "standard"
    | "designer"
    | "designer_alumni"
    | "education_student"
    | "education";
  /** Number of licenses to create */
  quantity?: number;
  /** Additional metadata for the subscription */
  metadata?: {
    [key: string]: string;
  };
  /** Optional discount code to apply to the trial subscription */
  discountCode?: string;
};
export type VwpCustomerRedirectResponse = {
  /** URL to redirect to Stripe admin panel for customer management */
  redirectUrl: string;
};
export type VwpHubspotIdentifyUserResponse = {
  /** an email address that can be used to identify the current user in hubspot */
  identificationEmail?: string;
  /** a session token that can be used to identify the current user in hubspot */
  identificationToken?: string;
};
export type UserRole = "PLATFORM_ADMIN" | "ADMIN" | "USER" | "STUDENT";
export type TeamStatus = "ACTIVE" | "INACTIVE" | "SUSPENDED";
export type AccountStatus = "ACTIVE" | "DISABLED";
export type VwpTeamMemberLicense = {
  /** Unique license identifier */
  id: string;
  /** License display name */
  name: string;
  /** Product identifier */
  productId: string;
};
export type VwpTeamMember = {
  /** Unique member identifier */
  id: number;
  /** Member's first name */
  firstName: string;
  /** Member's last name */
  lastName: string;
  /** Member email address */
  email: string;
  /** Member role in the team */
  role: string;
  /** Date when member joined the team */
  joinedDate: string;
  /** Last platform login timestamp from license server */
  lastPlatformLogin?: string;
  /** Last license login timestamp from license server */
  lastLicenseLogin?: string;
  /** Last used POLYSUN version from license server */
  lastUsedVersion?: string;
  status: AccountStatus;
  /** List of licenses assigned to the member */
  licenses: VwpTeamMemberLicense[];
};
export type LicenseStatus = "ACTIVE" | "DISABLED";
export type VwpEntitlement = {
  /** Unique entitlement identifier */
  id: string;
  /** Entitlement name */
  name: string;
  value?: string;
  softLimit?: number;
  hardLimit?: number;
};
export type TrialStatus = "NO_TRIAL" | "IN_TRIAL" | "TRIAL_COMPLETED";
export type SupportLevel = "NONE" | "SMA" | "SMA_PLUS";
export type VwpQuota = {
  entitlement?: string;
  currentUsage?: number;
  /** Soft quota limit; usage above it warns but is still allowed */
  limit?: number;
  /** Hard quota limit; consumption is refused above it. Null means unlimited */
  hardLimit?: number;
  percentageUsed?: number;
  periodStart?: string;
  periodEnd?: string;
};
export type VwpLicense = {
  /** Unique license identifier */
  id: string;
  /** Product name or identifier */
  product: string;
  /** Product identifier for integration purposes */
  productId?: string;
  team?: VwpTeam;
  /** Total number of seats available in the license */
  quantity: number;
  /** Number of seats currently assigned to users */
  assigned: number;
  /** Number of seats currently available for assignment */
  available?: number;
  /** Support expiration date */
  supportExpiration?: string;
  /** Software expiration date */
  softwareExpiration?: string;
  /** Effective expiry date of the license */
  effectiveExpiryDate?: string;
  status: LicenseStatus;
  /** ISO 8601 timestamp when license was created */
  createdDate: string;
  /** List of entitlements or features included in the license */
  entitlements?: VwpEntitlement[];
  trialStatus?: TrialStatus;
  supportLevel?: SupportLevel;
  firstInvoiceId?: string;
  subscriptionId?: string;
  migrationCrmId?: string;
  oldPkid?: string;
  oldEid?: string;
  /** Current quota usage entries for this license */
  quotas?: VwpQuota[];
};
export type VwpLicenseItem = {
  /** Unique license item identifier */
  id?: string;
  /** Product identifier reference */
  productId: string;
  /** Product name */
  name: string;
  /** License item start date */
  startDate?: string;
  /** License item expiration date */
  expiryDate?: string;
  /** Effective expiry date of the license */
  effectiveExpiryDate?: string;
  /** Status of the license item */
  status: "ACTIVE" | "DISABLED";
  lastInvoiceItemId?: string;
  referencePurchaseId?: string;
  invoiceItemIdHistory?: string[];
};
export type VwpLicenseRead = {
  /** Unique license identifier */
  id: string;
  /** Product name or identifier */
  product: string;
  /** Product identifier for integration purposes */
  productId?: string;
  team?: VwpTeam;
  /** Total number of seats available in the license */
  quantity: number;
  /** Number of seats currently assigned to users */
  assigned: number;
  /** Number of seats currently available for assignment */
  available?: number;
  /** Support expiration date */
  supportExpiration?: string;
  /** Software expiration date */
  softwareExpiration?: string;
  /** Effective expiry date of the license */
  effectiveExpiryDate?: string;
  status: LicenseStatus;
  /** ISO 8601 timestamp when license was created */
  createdDate: string;
  /** List of entitlements or features included in the license */
  entitlements?: VwpEntitlement[];
  trialStatus?: TrialStatus;
  supportLevel?: SupportLevel;
  /** Array of license items (products and support) */
  items?: VwpLicenseItem[];
  firstInvoiceId?: string;
  subscriptionId?: string;
  migrationCrmId?: string;
  oldPkid?: string;
  oldEid?: string;
  /** Current quota usage entries for this license */
  quotas?: VwpQuota[];
};
export type VwpTeamCategory =
  | "UNIVERSITY"
  | "ALUMNI"
  | "STUDENT"
  | "PRIVATE"
  | "ENTERPRISE";
export type VwpEducationalSettings = {
  semesterStartDates?: string[];
  semesterEndDates?: string[];
  settingsLocked?: boolean;
};
export type VwpTeam = {
  /** Unique team identifier */
  id: string;
  /** Name of the team */
  name?: string;
  /** Name of the team administrator */
  adminName?: string;
  /** Email address of the team administrator */
  adminEmail?: string;
  streetAddress?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  phoneNumber?: string;
  billingEmail?: string;
  invoiceLanguage?: "en" | "de" | "fr";
  website?: string;
  /** Stripe customer ID associated with the team */
  stripeCustomerId?: string;
  /** VAT number for the team */
  vatNumber?: string;
  /** Number of members in the team */
  members?: number;
  /** Total number of licenses assigned to the team */
  totalLicenses?: number;
  /** ISO 8601 timestamp when the team was created */
  createdDate?: string;
  status?: TeamStatus;
  /** List of team members with their details */
  teamMembers?: VwpTeamMember[];
  /** List of licenses assigned to the team */
  licenses?: VwpLicense[];
  /** Team categories used for segmentation */
  categories?: VwpTeamCategory[];
  /** True when categories contain UNIVERSITY */
  isUniversity?: boolean;
  /** Maximum number of admins allowed for this team. Null means unlimited. */
  maxAdminCount?: number | null;
  educationalSettings?: VwpEducationalSettings;
};
export type VwpTeamRead = {
  /** Unique team identifier */
  id: string;
  /** Name of the team */
  name?: string;
  /** Name of the team administrator */
  adminName?: string;
  /** Email address of the team administrator */
  adminEmail?: string;
  streetAddress?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  phoneNumber?: string;
  billingEmail?: string;
  invoiceLanguage?: "en" | "de" | "fr";
  website?: string;
  /** Stripe customer ID associated with the team */
  stripeCustomerId?: string;
  /** VAT number for the team */
  vatNumber?: string;
  /** Number of members in the team */
  members?: number;
  /** Total number of licenses assigned to the team */
  totalLicenses?: number;
  /** ISO 8601 timestamp when the team was created */
  createdDate?: string;
  status?: TeamStatus;
  /** List of team members with their details */
  teamMembers?: VwpTeamMember[];
  /** List of licenses assigned to the team */
  licenses?: VwpLicenseRead[];
  /** Team categories used for segmentation */
  categories?: VwpTeamCategory[];
  /** True when categories contain UNIVERSITY */
  isUniversity?: boolean;
  /** Maximum number of admins allowed for this team. Null means unlimited. */
  maxAdminCount?: number | null;
  educationalSettings?: VwpEducationalSettings;
};
export type VwpUser = {
  /** Unique user identifier */
  id: number;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** User's email address */
  email: string;
  role: UserRole;
  team?: VwpTeam;
  status: AccountStatus;
  /** ISO 8601 timestamp when user joined */
  joinedDate: string;
  /** Last platform login timestamp from license server */
  lastPlatformLogin?: string;
  /** Last license login timestamp from license server */
  lastLicenseLogin?: string;
  /** Last used POLYSUN version from license server */
  lastUsedVersion?: string;
  /** Array of license objects */
  licenses?: VwpLicense[];
  /** Internal user identifier from the license server */
  internalId?: string;
  flags?: VwpUserFlag[];
};
export type VwpUserRead = {
  /** Unique user identifier */
  id: number;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** User's email address */
  email: string;
  role: UserRole;
  team?: VwpTeamRead;
  status: AccountStatus;
  /** ISO 8601 timestamp when user joined */
  joinedDate: string;
  /** Last platform login timestamp from license server */
  lastPlatformLogin?: string;
  /** Last license login timestamp from license server */
  lastLicenseLogin?: string;
  /** Last used POLYSUN version from license server */
  lastUsedVersion?: string;
  /** Array of license objects */
  licenses?: VwpLicenseRead[];
  /** Internal user identifier from the license server */
  internalId?: string;
  flags?: VwpUserFlag[];
};
export type VwpPagination = {
  /** Total number of items */
  total: number;
  /** Current page number (0-based) */
  page: number;
  /** Number of items per page */
  size: number;
  /** Total number of pages */
  totalPages: number;
};
export type VwpUserListResponse = {
  /** List of users */
  users?: VwpUser[];
  pagination?: VwpPagination;
};
export type VwpUserListResponseRead = {
  /** List of users */
  users?: VwpUserRead[];
  pagination?: VwpPagination;
};
export type VwpUserCreationRequest = {
  /** User's email address */
  email: string;
  /** Team identifier to assign user to */
  teamId: string;
  role: UserRole;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
};
export type VwpUserUpdateRequest = {
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** Internal user ID used by the license server */
  internalId?: string;
  role?: UserRole;
  status?: AccountStatus;
  flags?: VwpUserFlag[];
};
export type VwpUserEmailChangeRequest = {
  /** The new email address (applied to WorkOS and the license server) */
  email: string;
};
export type VwpUserTeamChangeResponse = {
  /** User ID */
  id: number;
  /** User email address */
  userEmail: string;
  /** User's first name */
  firstName: string;
  /** User's last name */
  lastName: string;
  /** New team ID after assignment */
  teamId?: string | null;
};
export type VwpUserTeamChangeRequest = {
  /** Team identifier to assign user to, or null to remove from team */
  teamId?: string | null;
};
export type VwpUpdateUserRoleRequest = {
  role: UserRole;
};
export type VwpTeamMembers = {
  /** List of team members */
  members: VwpTeamMember[];
  pagination: VwpPagination;
};
export type VwpTeamResponse = {
  team: VwpTeam;
  teamMembers: VwpTeamMembers;
};
export type VwpTeamResponseRead = {
  team: VwpTeamRead;
  teamMembers: VwpTeamMembers;
};
export type VwpEducationalSettingsUpdateRequest = {
  semesterStartDates?: string[];
  semesterEndDates?: string[];
};
export type VwpTeamUpdateRequest = {
  /** Team name */
  name?: string;
  billingStreetAddress?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  billingCountry?: string;
  billingEmail?: string;
  invoiceLanguage?: "en" | "de" | "fr";
  website?: string;
  phone?: string;
  /** VAT number for the team */
  vatNumber?: string;
  /** Stripe customer ID associated with the team */
  stripeCustomerId?: string;
  /** Team categories used for segmentation */
  categories?: VwpTeamCategory[];
  /** Maximum number of admins allowed for this team. Null means unlimited. */
  maxAdminCount?: number | null;
  educationalSettings?: VwpEducationalSettingsUpdateRequest;
};
export type VwpArchivedMember = {
  /** Unique identifier for the archived member snapshot */
  id: number;
  /** Email address of the archived member */
  userEmail: string;
  /** Timestamp when the member was removed from the team */
  removedAt: string;
  /** Whether this archived member is visible */
  visibility?: boolean;
  /** Snapshot of member data at the time of removal */
  snapshot?: object;
};
export type VwpArchivedMembers = {
  /** List of archived team members */
  members: VwpArchivedMember[];
  pagination: VwpPagination;
};
export type VwpRemoveLicenseAssignmentResponse = {
  /** Success message describing the operation result */
  message: string;
  updatedLicense?: VwpLicense;
};
export type VwpRemoveLicenseAssignmentResponseRead = {
  /** Success message describing the operation result */
  message: string;
  updatedLicense?: VwpLicenseRead;
};
export type VwpRemoveLicenseAssignmentRequest = {
  /** Email of the user to remove the license from */
  userEmail: string;
  /** ID of the license to remove */
  licenseId: string;
};
export type VwpAddLicenseAssignmentResponse = {
  /** Success message describing the operation result */
  message: string;
  updatedLicense?: VwpLicense;
};
export type VwpAddLicenseAssignmentResponseRead = {
  /** Success message describing the operation result */
  message: string;
  updatedLicense?: VwpLicenseRead;
};
export type VwpAddLicenseAssignmentRequest = {
  /** Email of the user to assign the license to */
  userEmail: string;
  /** ID of the license to assign */
  licenseId: string;
};
export type VwpLicenseListResponse = {
  /** List of licenses */
  licenses?: VwpLicense[];
  pagination?: VwpPagination;
  totalAvailableSeats?: number;
  totalAssignedSeats?: number;
  expiringSoon?: number;
};
export type VwpLicenseListResponseRead = {
  /** List of licenses */
  licenses?: VwpLicenseRead[];
  pagination?: VwpPagination;
  totalAvailableSeats?: number;
  totalAssignedSeats?: number;
  expiringSoon?: number;
};
export type VwpCreateLicenseRequest = {
  /** Product identifier for license */
  productId: string;
  /** Number of seats for the license */
  quantity: number;
  /** Team identifier to assign license to */
  teamId: string;
  /** Optional expiration date for the license */
  expires?: string;
};
export type VwpUpdateLicenseItem = {
  /** Unique license item identifier; when set the existing item is updated in place, otherwise a new item is created */
  id?: string;
  /** Product identifier reference */
  productId: string;
  /** License item start date */
  startDate?: string;
  /** License item expiration date */
  expiryDate?: string;
  /** Status of license item */
  status: "ACTIVE" | "DISABLED";
  lastInvoiceItemId?: string;
  referencePurchaseId?: string;
  invoiceItemIdHistory?: string[];
};
export type VwpUpdateLicenseRequest = {
  /** Number of seats for the license */
  quantity: number;
  status: LicenseStatus;
  trialStatus?: TrialStatus;
  /** List of entitlements for the license */
  entitlements?: VwpEntitlement[];
  firstInvoiceId?: string;
  subscriptionId?: string;
  /** License items to update */
  items?: VwpUpdateLicenseItem[];
};
export type VwpUpdateQuotaUsageRequest = {
  /** New value for the quota usage counter */
  currentUsage: number;
};
export type VwpAssignLicenseToTeamResponse = {
  updatedLicense: VwpLicense;
};
export type VwpAssignLicenseToTeamResponseRead = {
  updatedLicense: VwpLicenseRead;
};
export type VwpAssignLicenseToTeamRequest = {
  /** Team identifier to assign license to */
  teamId: string;
};
export type VwpStripeUrlResponse = {
  url: string;
};
export type VwpMigrateLicenseToStripeRequest = {
  /** Stripe product id (prod_...) to create the recurring subscription on */
  stripeProductId: string;
  /** Currency for the migrated SMA subscription (EUR or CHF) */
  smaCurrency?: string;
  /** Total recurring price as a currency amount with up to 2 decimal places (e.g. 99.99 for CHF 99.99 total). The per-unit amount in cents is derived by dividing this by quantity. */
  customSmaPrice: number;
  /** Number of units the subscription is created for. The total price must be evenly divisible by this in cents. */
  quantity: number;
};
export type VwpTeamListResponse = {
  /** List of teams */
  teams?: VwpTeam[];
  pagination?: VwpPagination;
};
export type VwpTeamListResponseRead = {
  /** List of teams */
  teams?: VwpTeamRead[];
  pagination?: VwpPagination;
};
export type VwpCreateTeamResponse = {
  team: VwpTeam;
};
export type VwpCreateTeamResponseRead = {
  team: VwpTeamRead;
};
export type VwpCreateTeamRequest = {
  /** Team name for the new team */
  name: string;
  /** Stripe customer ID associated with the team */
  stripeCustomerId?: string;
  /** VAT number for the team */
  vatNumber?: string;
};
export type VwpProduct = {
  /** Unique product identifier */
  id: string;
  /** Product name */
  name: string;
  /** Product description */
  description?: string;
  /** Stripe product identifier */
  stripeProductId: string;
  /** Total number of licenses for this product */
  totalLicenses: number;
  /** Number of active subscriptions */
  activeSubscriptions: number;
  /** Array of entitlement objects */
  entitlements?: VwpEntitlement[];
  softwareDurationDays?: number;
  group?: string;
  priority?: number;
};
export type VwpProductListResponse = {
  /** List of products */
  products?: VwpProduct[];
  pagination?: VwpPagination;
};
export type VwpUpdateProductRequest = {
  name?: string;
  stripeProductId?: string;
  entitlements?: VwpEntitlement[];
  softwareDurationDays?: number;
  group?: string;
  priority?: number;
};
export type VwpGenerateInvitationResponse = {
  /** JWT invitation code */
  invitationCode?: string;
};
export type VwpGenerateInvitationRequest = {
  /** Optional license ID to associate with invitation */
  licenseId: string;
};
export type VwpRedeemInvitationResponse = {
  /** Success message */
  message?: string;
  /** ID of team user was added to */
  teamId?: string;
};
export type VwpRedeemInvitationRequest = {
  /** JWT invitation code to redeem */
  invitationCode: string;
};
export type VwpFeatureConfig = {
  /** Whether any active license of the current user's team is not limited by its quantity */
  hasUnlimitedSeats?: boolean;
  educationalNewMembersPerPeriod?: number;
  /** Maximum number of admins allowed for the current user's team. Null means unlimited. */
  teamAdminCapacity?: number;
  /** Whether the current user is eligible for alumni benefits */
  isAlumniEligible?: boolean;
};
export type VwpAlumniAcceptInitiateRequest = {
  /** New personal email address to verify and use for alumni status */
  newEmail: string;
};
export type VwpAlumniAcceptConfirmRequest = {
  /** One-time code from the verification email sent to the new personal address */
  code: string;
};
export type VwpEntitlementsResponse = {
  /** List of all available entitlement names */
  availableEntitlements?: string[];
};
export type VwpWebhookInvoiceItem = {
  /** Stripe product id (must be a known product or the support/SMA product) */
  productId: string;
  /** Unit amount in major currency units with up to 2 decimals (e.g. 175.10 means CHF 175.10) */
  price: number;
  /** Number of units for this line item */
  quantity?: number;
  /** Optional discount as a percentage (0-100). Only one of discountPercentage or discountValue can be provided. */
  discountPercentage?: number;
  /** Optional fixed unit discount amount in major units with up to 2 decimals (e.g. 10.50 means CHF 10.50 discount per unit). Only one of discountPercentage or discountValue can be provided. */
  discountValue?: number;
};
export type VwpWebhookCreateInvoiceRequest = {
  /** License server team id used to resolve or create the Stripe customer */
  teamId: string;
  /** ISO 4217 currency code for all line items (must be supported, e.g. EUR or CHF) */
  currency: string;
  /** ISO 8601 date-time. Must be today or in the future. When an SMA/support product is present it is also used as the subscription billing cycle anchor; otherwise stored as metadata only. */
  startDate: string;
  /** Optional ISO 8601 date-time. Currently ignored except for logging/metadata. */
  endDate?: string;
  items: VwpWebhookInvoiceItem[];
  /** Optional additional metadata attached to the created Stripe resources */
  metadata?: {
    [key: string]: string;
  };
};
export const {
  useInitiateLoginQuery,
  useHandleOAuthCallbackQuery,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useRefreshTokenMutation,
  useBypassAuthenticationMutation,
  useChangePasswordMutation,
  useUpdateProfileMutation,
  useAcceptTosMutation,
  useSetup2FaMutation,
  useEnable2FaMutation,
  useDisable2FaMutation,
  useInitiateEmailChangeMutation,
  useGetEmailChangeStatusQuery,
  useConfirmEmailChangeMutation,
  useCancelEmailChangeMutation,
  useCreateCheckoutSessionMutation,
  useGetProductsQuery,
  useGetPricingQuery,
  useCreateBillingPortalSessionMutation,
  useCreateInvoiceSubscriptionMutation,
  useCreateTrialSubscriptionMutation,
  useGetCustomerRedirectUrlQuery,
  useIdentifyHubspotUserMutation,
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserByIdMutation,
  useUpdateUserEmailByIdMutation,
  useAssignUserToTeamMutation,
  useUpdateUserRoleMutation,
  useRemoveTeamMemberMutation,
  useGetPortalTeamQuery,
  useUpdatePortalTeamMutation,
  useGetPortalTeamArchivedMembersQuery,
  useRemoveLicenseAssignmentMutation,
  useAddLicenseAssignmentMutation,
  useGetPortalLicensesQuery,
  useGetLicensesQuery,
  useCreateLicenseMutation,
  useGetLicenseByIdQuery,
  useUpdateLicenseByIdMutation,
  useUpdateLicenseQuotaUsageMutation,
  useAssignLicenseToTeamMutation,
  useGetLicenseStripeUrlQuery,
  useMigrateLicenseToStripeMutation,
  useRemoveTeamMemberByAdminMutation,
  useGetTeamsQuery,
  useCreateTeamMutation,
  useGetTeamByIdQuery,
  useUpdateTeamByIdMutation,
  useGetTeamArchivedMembersByIdQuery,
  useGetAdminProductsQuery,
  useUpdateProductByIdMutation,
  useAddTeamMemberLicenseMutation,
  useRemoveTeamMemberLicenseMutation,
  useGenerateInvitationMutation,
  useRedeemInvitationMutation,
  useGetPortalConfigQuery,
  useInitiateAlumniAcceptanceMutation,
  useGetAlumniAcceptanceStatusQuery,
  useConfirmAlumniAcceptanceMutation,
  useCancelAlumniAcceptanceMutation,
  useGetWebsiteContentQuery,
  useGetAdminEntitlementsQuery,
  useCreateWebhookStripeInvoiceMutation,
} = injectedRtkApi;
